import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Shift } from '../../models/business/PolicyShift'
import { shiftSchema } from '../../models/schema'
import {
  createShift as createShiftAPI,
  deleteShift as deleteShiftAPI,
  fetchShiftById,
  fetchShifts,
  updateShift as updateShiftAPI,
} from '../../services/business/ShiftService'
import { AppDispatch, RootState } from './../store'

const initialState: SBState<Shift> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'shift',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getShiftsSuccess,
  setQuery: setShiftQuery,
  reset: resetShiftState,
  resetQueryAndIds: resetShiftQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Shift }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectShifts = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )

export const selectAllShifts = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )
export const selectSelectedShift = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectShiftById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectShiftsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedShift =
  (shift: Shift | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Shift>(shift)))

export const getShifts = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchShifts(params),
    [shiftSchema],
    slice.actions
  )

export const createShift = (shift: Shift) =>
  SBAPI.CreateDispatched(createShiftAPI(shift), shiftSchema, slice.actions)

export const getShiftWithId = (id: number) =>
  SBAPI.FetchDispatched<Shift>(fetchShiftById(id), shiftSchema, slice.actions)

export const updateShift = (shift: Shift) =>
  SBAPI.UpdateDispatched<Shift>(updateShiftAPI(shift), shift, slice.actions)

export const deleteShift = (shift: Shift) =>
  SBAPI.DestroyDispatched<Shift>(deleteShiftAPI(shift), shift, slice.actions)
